import React, { useEffect, useState, useRef } from 'react';
import { aiApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Sparkles, Send, Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';

const SUGGESTIONS_EMPRESA = [
  '¿Cómo ha evolucionado mi producción respecto al mes pasado?',
  '¿Estoy por encima o por debajo de la media del sector?',
  '¿Qué indicador ha empeorado más recientemente?',
  '¿Qué dato mío parece anómalo?',
];
const SUGGESTIONS_ADMIN = [
  'Resumen del sector en el último periodo',
  '¿Cuáles son los indicadores con mayor variación?',
  '¿Qué categorías tienen más datos aportados?',
  '¿Qué campañas están abiertas?',
];

export default function AIAgent() {
  const { user, isEmpresa } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `${Date.now()}`);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async (text) => {
    const msg = text || input;
    if (!msg.trim()) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const r = await aiApi.chat(sessionId, msg);
      setMessages(m => [...m, { role: 'assistant', content: r.data.response }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error del agente IA');
      setMessages(m => [...m, { role: 'assistant', content: '⚠️ Se ha producido un error. Inténtelo de nuevo.' }]);
    } finally { setLoading(false); }
  };

  const suggestions = isEmpresa ? SUGGESTIONS_EMPRESA : SUGGESTIONS_ADMIN;

  return (
    <div className="max-w-4xl mx-auto animate-fade-in-up">
      <Card className="aea-card">
        <CardContent className="p-0">
          {/* Header */}
          <div className="p-5 border-b border-slate-200 flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-aea-blue/10 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-aea-blue" />
            </div>
            <div className="flex-1">
              <div className="font-outfit font-semibold text-lg">Agente IA · Espacio de Datos AEA</div>
              <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                <Shield className="w-3 h-3" /> Solo utiliza datos {isEmpresa ? 'suyos' : 'del sector'} y agregados anónimos
              </div>
            </div>
            <div className="text-xs text-slate-400 font-mono">gemini-3-flash</div>
          </div>

          {/* Messages */}
          <div className="h-[500px] overflow-y-auto p-5 bg-slate-50/50 space-y-3">
            {messages.length === 0 && (
              <div className="text-center py-10">
                <Sparkles className="w-10 h-10 mx-auto text-aea-blue/40 mb-3" />
                <div className="text-slate-500 mb-4">Pregunte al agente. Responderá con sus datos y comparativas anonimizadas del sector.</div>
                <div className="grid gap-2 max-w-md mx-auto">
                  {suggestions.map((s, i) => (
                    <button key={i} onClick={() => send(s)}
                      data-testid={`ai-suggestion-${i}`}
                      className="text-sm text-left px-4 py-2.5 rounded-lg border border-slate-200 hover:border-aea-blue hover:bg-white transition-all">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={m.role === 'user' ? 'chat-user' : 'chat-ai'}>
                  <div className="whitespace-pre-wrap text-sm leading-relaxed">{m.content}</div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start"><div className="chat-ai flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> <span className="text-sm">Pensando...</span></div></div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-slate-200 flex gap-2">
            <Input
              placeholder="Escriba su pregunta..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())}
              disabled={loading}
              data-testid="ai-input"
              className="flex-1"
            />
            <Button onClick={() => send()} disabled={loading || !input.trim()} className="bg-aea-blue hover:bg-aea-blue-dk" data-testid="ai-send-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
